import { type ComponentProps, type ReactNode, isValidElement } from 'react'
import {
  CheckCircleIcon,
  ChevronDownIcon,
  CircleIcon,
  ClockIcon,
  WrenchIcon,
  XCircleIcon,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/shared/lib/utils'
import { CodeBlock } from './code-block'

export type ToolProps = ComponentProps<typeof Collapsible>

export function Tool({ className, ...props }: ToolProps) {
  return (
    <Collapsible
      className={cn('not-prose mb-4 w-full rounded-md border', className)}
      {...props}
    />
  )
}

/**
 * Subset of `ToolUIPart['state']` we render. Kept as a literal union because
 * `ToolUIPart` from AI SDK v6 is generic over `UITools` and resolves to `never`
 * without explicit type parameters.
 */
export type ToolState =
  | 'input-streaming'
  | 'input-available'
  | 'approval-requested'
  | 'approval-responded'
  | 'output-available'
  | 'output-error'
  | 'output-denied'

/**
 * Type signature accepted for tool part `type` strings. AI SDK v6 emits either
 * `tool-${name}` (static tools) or `'dynamic-tool'` (dynamic tools).
 */
export type ToolType = `tool-${string}` | 'dynamic-tool'

const STATUS_LABELS: Record<ToolState, string> = {
  'input-streaming': 'Pending',
  'input-available': 'Running',
  'approval-requested': 'Awaiting Approval',
  'approval-responded': 'Responded',
  'output-available': 'Completed',
  'output-error': 'Error',
  'output-denied': 'Denied',
}

const STATUS_ICONS: Record<ToolState, ReactNode> = {
  'input-streaming': <CircleIcon className='size-4' />,
  'input-available': <ClockIcon className='size-4 animate-pulse' />,
  'approval-requested': <ClockIcon className='size-4 text-yellow-600' />,
  'approval-responded': <CheckCircleIcon className='size-4 text-blue-600' />,
  'output-available': <CheckCircleIcon className='size-4 text-green-600' />,
  'output-error': <XCircleIcon className='size-4 text-red-600' />,
  'output-denied': <XCircleIcon className='size-4 text-orange-600' />,
}

export function getStatusBadge(state: ToolState, label?: ReactNode) {
  return (
    <Badge className='gap-1.5 rounded-full text-xs' variant='secondary'>
      {STATUS_ICONS[state]}
      {label ?? STATUS_LABELS[state]}
    </Badge>
  )
}

export type ToolHeaderProps = Omit<
  ComponentProps<typeof CollapsibleTrigger>,
  'type'
> & {
  title?: string
  type: ToolType
  state: ToolState
  /** Optional override to render the status badge (e.g. for i18n) */
  renderStatusBadge?: (state: ToolState) => ReactNode
}

export function ToolHeader({
  className,
  title,
  type,
  state,
  renderStatusBadge,
  ...props
}: ToolHeaderProps) {
  return (
    <CollapsibleTrigger
      className={cn(
        'group flex w-full items-center justify-between gap-4 p-3',
        className
      )}
      {...props}
    >
      <div className='flex items-center gap-2'>
        <WrenchIcon className='size-4 text-muted-foreground' />
        <span className='text-sm font-medium'>
          {title ?? type.split('-').slice(1).join('-')}
        </span>
        {renderStatusBadge ? renderStatusBadge(state) : getStatusBadge(state)}
      </div>
      <ChevronDownIcon className='size-4 text-muted-foreground transition-transform group-data-panel-open:rotate-180' />
    </CollapsibleTrigger>
  )
}

export type ToolContentProps = ComponentProps<typeof CollapsibleContent>

export function ToolContent({ className, ...props }: ToolContentProps) {
  return (
    <CollapsibleContent
      className={cn(
        'text-popover-foreground outline-none',
        className
      )}
      {...props}
    />
  )
}

export type ToolInputProps = ComponentProps<'div'> & {
  input: unknown
  /** Optional override for the "Parameters" label */
  parametersLabel?: ReactNode
}

export function ToolInput({
  className,
  input,
  parametersLabel,
  ...props
}: ToolInputProps) {
  return (
    <div className={cn('space-y-2 overflow-hidden p-4', className)} {...props}>
      <h4 className='text-xs font-medium tracking-wide text-muted-foreground uppercase'>
        {parametersLabel ?? 'Parameters'}
      </h4>
      <div className='rounded-md bg-muted/50'>
        <CodeBlock code={JSON.stringify(input, null, 2)} language='json' />
      </div>
    </div>
  )
}

export type ToolOutputProps = ComponentProps<'div'> & {
  output: unknown
  errorText: string | null | undefined
  /** Optional override for the "Result" label */
  resultLabel?: ReactNode
  /** Optional override for the "Error" label */
  errorLabel?: ReactNode
}

export function ToolOutput({
  className,
  output,
  errorText,
  resultLabel,
  errorLabel,
  ...props
}: ToolOutputProps) {
  if (!output && !errorText) {
    return null
  }

  let renderedOutput: ReactNode = <div>{output as ReactNode}</div>
  if (typeof output === 'object' && !isValidElement(output)) {
    renderedOutput = (
      <CodeBlock code={JSON.stringify(output, null, 2)} language='json' />
    )
  } else if (typeof output === 'string') {
    renderedOutput = <CodeBlock code={output} language='json' />
  }

  return (
    <div className={cn('space-y-2 p-4', className)} {...props}>
      <h4 className='text-xs font-medium tracking-wide text-muted-foreground uppercase'>
        {errorText ? (errorLabel ?? 'Error') : (resultLabel ?? 'Result')}
      </h4>
      <div
        className={cn(
          'overflow-x-auto rounded-md text-xs [&_table]:w-full',
          errorText
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted/50 text-foreground'
        )}
      >
        {errorText ? <div>{errorText}</div> : null}
        {renderedOutput}
      </div>
    </div>
  )
}
